/* Эндпоинты первой очереди (план, этап 5): вход, профиль, баланс, каталог голосов, голос озвучки чата.
   База — VITE_API_BASE (по умолчанию /bot/v1 этого же адреса: в разработке и проверках его отвечает MSW, src/mocks). */
import { createClient } from './client';
import { createUploadSlot, uploadFile, waitForUpload } from './uploads';
import { userVoices } from './user-voices';
import { speech } from './speech';
import { people } from './people';
import { scripts } from './scripts';
import { account } from './account';
import { avatarCatalog } from './avatar-catalog';
import { finance } from './finance';
import { helpContent } from './help-content';
import { history } from './history';
import { commerce } from './commerce';
import { rewards } from './rewards';
import { partner } from './partner';
import type { UploadIntent } from './uploads';
import { platform } from '../platform';
import type { Balance, ChatTtsEventsPage, ChatTtsOrder, ChatTtsOrdersPage, Favorites, LocalHistoryStats,
  Me, SessionResponse, TtsEngine, TtsVoiceResponse, VoiceCollectionsPage, VoicesPage, VoicesQuery } from './types';

let start: (() => Promise<unknown>) | null = null;
let ready: Promise<unknown> | null = null;
/** Что сделать перед первым запросом (запуск моков в разработке). Ленивое: пока API не нужен, страница ничего не запускает
    и не тратит Math.random — кадры проверок с одинаковым зерном совпадают с прототипом */
export const setApiReady = (fn: () => Promise<unknown>) => void (start = fn);
const whenReady = () => (ready ??= start ? start().catch(() => {}) : Promise.resolve());

export const api = createClient({
  base: import.meta.env.VITE_API_BASE || './bot/v1',
  initData: () => platform.initData(),
  platform: () => platform.kind,
  lang: () => document.documentElement.lang || 'ru',
  ready: whenReady,
});

let meFlight: { epoch: number; value: Promise<Me> } | null = null;

export const endpoints = {
  /** Поколение сессии клиента (client.epoch): ключ кэшей авторизованных картинок. */
  sessionEpoch: (): number => api.epoch,
  partner: partner(api),
  rewards: rewards(api),
  commerce: commerce(api),
  history: history(api),
  speech: speech(api),
  userVoices: userVoices(api),
  people: people(api),
  scripts: scripts(api),
  account: account(api),
  avatarCatalog: avatarCatalog(api),
  finance: finance(api),
  helpContent: helpContent(api),
  session: (): Promise<SessionResponse> => api.login(),
  /** Одновременные чтения профиля делят один запрос: на стенде /app/me отвечает ~2 с, а экран Студии
      открывает его из нескольких хуков сразу. Ответ не кэшируется — следующий вызов идёт на сервер. */
  me: () => {
    if (meFlight && meFlight.epoch === api.epoch) return meFlight.value;
    const flight = { epoch: api.epoch, value: api.request<Me>('/app/me') };
    meFlight = flight;
    void flight.value.catch(() => {}).finally(() => {
      if (meFlight === flight) meFlight = null;
    });
    return flight.value;
  },
  balance: () => api.request<Balance>('/app/balance'),
  starsProduct: () => api.request<{ price_stars: number; grant_circle: number; price_version: string; available: boolean }>('/app/stars/product'),
  starsCheckout: (key: string) => api.request<{ order_id: string; payment_url: string; price_stars: number; grant_tokens: number }>('/app/stars/checkout', {
    method: 'POST', idempotency: key,
  }),
  starsOrder: (id: string) => api.request<{ state: string }>(`/app/stars/orders/${encodeURIComponent(id)}`),
  starsPurchases: () => api.request<{ items: { order_id: string; state: string; stars: number;
    circle: number; received_at: string; fulfilled_at: string | null }[] }>('/app/stars/purchases'),
  voices: (q: VoicesQuery, signal?: AbortSignal) =>
    api.request<VoicesPage>('/app/catalog/voices', { query: { ...q }, signal }),
  voiceDetails: (id: number, signal?: AbortSignal) => api.request<import('./types').VoiceDetails>(`/app/catalog/voices/${id}`, { signal }),
  voiceCollections: (cursor?: string, limit = 50) =>
    api.request<VoiceCollectionsPage>('/app/catalog/voice-collections', { query: { cursor, limit } }),
  /** Избранное (сердце в карточках голоса и персонажа): голоса — карточками каталога, персонажи — id; новые первыми */
  favorites: (signal?: AbortSignal) => api.request<Favorites>('/app/favorites', { signal }),
  setFavorite: (kind: 'voices' | 'avatars', id: number, on: boolean) =>
    api.request<void>(`/app/favorites/${kind}/${id}`, { method: on ? 'PUT' : 'DELETE' }),
  /** tts_engine не передан — сервер ставит STEOS v2 */
  setTtsVoice: (voice_id: number, tts_engine?: TtsEngine) => api.request<TtsVoiceResponse>('/app/tts/voice',
    { method: 'PUT', body: tts_engine ? { voice_id, tts_engine } : { voice_id } }),
  createChatTts: (text: string, idempotencyKey: string) => api.request<ChatTtsOrder>('/app/orders', {
    method: 'POST', body: { text }, idempotency: idempotencyKey,
  }),
  chatTtsOrder: (id: number) => api.request<ChatTtsOrder>(`/app/orders/${id}`),
  chatTtsOrders: async (cursor?: string) => {
    const page = await api.request<ChatTtsOrdersPage>('/app/orders', { query: { cursor, limit: 20 } });
    return { ...page, items: page.items.filter(item => item.kind === 'tts') };
  },
  localHistoryStats: () => api.request<LocalHistoryStats>('/app/history/local/stats'),
  activeChatTts: async () => {
    const page = await api.request<{ items: ChatTtsOrder[] }>('/app/orders/active');
    return { items: page.items.filter(item => item.kind === 'tts') };
  },
  chatTtsEvents: (id: number, after: number, signal?: AbortSignal) =>
    api.request<ChatTtsEventsPage>(`/app/orders/${id}/events`, {
      query: { after, wait_ms: 25000 }, signal,
    }),
  seenChatTts: (id: number) => api.request<void>(`/app/orders/${id}/seen`, { method: 'POST' }),
  hideChatTts: (id: number) => api.request<void>(`/app/orders/${id}/hide`, { method: 'POST' }),
  sendChatTts: (id: number, format: 'audio' | 'document', idempotencyKey: string) =>
    api.request<ChatTtsOrder>(`/app/orders/${id}/send`, {
      method: 'POST', body: { format }, idempotency: idempotencyKey,
    }),
  chatTtsAudio: (id: number, signal?: AbortSignal) => api.download(`/app/orders/${id}/result/audio`, signal),
  createUploadSlot: (file: File, intent: UploadIntent, idempotencyKey: string, signal?: AbortSignal) =>
    createUploadSlot(api, file, intent, idempotencyKey, signal),
  uploadFile: (file: File, intent: UploadIntent, idempotencyKey: string, signal?: AbortSignal) =>
    uploadFile(api, file, intent, { idempotencyKey, signal }),
  uploadStatus: (id: string, signal?: AbortSignal) => api.request<import('./types').UploadStatusResponse>(`/app/uploads/${encodeURIComponent(id)}`, { signal }),
  uploadPreview: (id: string, signal?: AbortSignal) => api.download(`/app/uploads/${encodeURIComponent(id)}/preview`, signal),
  waitForUpload: (id: string, signal?: AbortSignal) => waitForUpload(api, id, signal),
};
